import React from 'react';
import { Activity, ShieldAlert, HeartPulse, Sparkles, Clock, Bell, UserCheck, Building2, KeyRound, Radio } from 'lucide-react';
import { ClinicalBusiness, UserProfile } from '../types';

interface NavbarProps {
  systemStatus: string;
  onQuickSimulateAlert: () => void;
  unresolvedAlertCount: number;
  currentBusiness?: ClinicalBusiness;
  currentUser?: UserProfile;
  onOpenAccessModal?: () => void;
  onResetDrill?: () => void;
  onNavigateToAmbient?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  systemStatus,
  onQuickSimulateAlert,
  unresolvedAlertCount,
  currentBusiness,
  currentUser,
  onOpenAccessModal,
  onResetDrill,
  onNavigateToAmbient
}) => {
  const [time, setTime] = React.useState<string>('');

  React.useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' EST');
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, []);

  const isBetaTrial = currentBusiness?.licenseTier === 'BETA_TRIAL';

  return (
    <header className="h-16 border-b border-slate-800 bg-slate-900/90 backdrop-blur-md px-6 flex items-center justify-between sticky top-0 z-30">
      {/* Brand & Hospital Node */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-teal-500 to-cyan-400 p-0.5 flex items-center justify-center shadow-lg shadow-teal-500/20">
          <div className="h-full w-full bg-slate-950 rounded-[10px] flex items-center justify-center">
            <HeartPulse className="h-5 w-5 text-teal-400 animate-pulse" />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-bold tracking-wider text-slate-100 text-base">MEDICORE</span>
            <span className="bg-teal-500/20 text-teal-300 border border-teal-500/40 text-[10px] px-1.5 py-0.5 rounded font-mono font-semibold">
              UNIFIED OS 4.8
            </span>
          </div>
          <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-ping"></span>
            {currentBusiness ? currentBusiness.name : 'Metropolitan Memorial Hub • Tier-1 Trauma Network'}
          </p>
        </div>
      </div>

      {/* Center Live Metrics & Business License Status */}
      <div className="hidden lg:flex items-center gap-4 text-xs">
        {/* Business Selector Pill Button */}
        {currentBusiness && (
          <button
            onClick={onOpenAccessModal}
            className="flex items-center gap-2 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 px-3 py-1.5 rounded-xl transition text-left group"
            title="Click to switch clinical business or manage license"
          >
            <Building2 className="h-3.5 w-3.5 text-teal-400 group-hover:scale-110 transition" />
            <div className="flex flex-col">
              <span className="font-bold text-white text-[11px] max-w-[170px] truncate leading-tight">
                {currentBusiness.name}
              </span>
              <span className="text-[9px] font-mono text-slate-400">
                {currentBusiness.facilityType}
              </span>
            </div>
            <span
              className={`text-[9px] font-mono font-bold px-1.5 py-0.2 rounded ml-1 ${
                isBetaTrial
                  ? 'bg-amber-400/20 text-amber-300 border border-amber-400/40'
                  : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
              }`}
            >
              {isBetaTrial ? `BETA (${currentBusiness.trialDaysRemaining}d)` : 'ENTERPRISE'}
            </span>
          </button>
        )}

        <div className="flex items-center gap-2 bg-slate-800/60 border border-slate-700/60 px-3 py-1.5 rounded-lg">
          <Clock className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-mono text-slate-200">{time || '08:00:00 EST'}</span>
        </div>

        <div
          onClick={onResetDrill}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition select-none ${
            systemStatus.includes('DEFCON-2')
              ? 'bg-rose-500/20 border-rose-500/50 text-rose-300 animate-pulse cursor-pointer hover:bg-rose-500/30'
              : systemStatus.includes('DEFCON-3')
              ? 'bg-amber-500/20 border-amber-500/50 text-amber-300 cursor-pointer hover:bg-amber-500/30'
              : 'bg-slate-800/60 border-slate-700/60 text-slate-300'
          }`}
          title={systemStatus.includes('NORMAL') ? 'System Normal: DEFCON-4' : 'Drill Simulation Active • Click to Normalize'}
        >
          <Activity className={`h-3.5 w-3.5 ${systemStatus.includes('NORMAL') ? 'text-teal-400' : 'text-rose-400 animate-ping'}`} />
          <span className="text-slate-400">DEFCON:</span>
          <span className={`font-semibold font-mono text-xs ${systemStatus.includes('NORMAL') ? 'text-emerald-400' : 'text-rose-300 font-bold'}`}>
            {systemStatus}
          </span>
          {!systemStatus.includes('NORMAL') && (
            <span className="text-[9px] font-mono uppercase bg-rose-500 text-white px-1.5 py-0.2 rounded ml-1">
              Reset
            </span>
          )}
        </div>
      </div>

      {/* Right Controls & User Profile */}
      <div className="flex items-center gap-2.5">
        {onNavigateToAmbient && (
          <button
            onClick={onNavigateToAmbient}
            className="flex items-center gap-1.5 text-xs bg-teal-500/15 hover:bg-teal-500/25 text-teal-300 border border-teal-500/40 px-3 py-1.5 rounded-xl font-bold transition shadow-sm shadow-teal-500/10 active:scale-95"
            title="Go to Ambient Safety Hub (Aqara mmWave Radars & Fall Detection)"
          >
            <Radio className="h-3.5 w-3.5 text-teal-400 animate-pulse" />
            <span className="font-semibold">Ambient Safety / Aqara</span>
          </button>
        )}

        <button
          onClick={onQuickSimulateAlert}
          className="flex items-center gap-1.5 text-xs bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 px-3 py-1.5 rounded-lg transition"
          title="Simulate rapid triage telemetry change"
        >
          <ShieldAlert className="h-3.5 w-3.5 text-rose-400" />
          <span className="hidden sm:inline font-medium">Trigger Drill</span>
        </button>

        <div className="relative">
          <div className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:text-white transition cursor-pointer">
            <Bell className="h-4 w-4" />
            {unresolvedAlertCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-rose-500 text-[10px] font-bold text-white flex items-center justify-center">
                {unresolvedAlertCount}
              </span>
            )}
          </div>
        </div>

        <div className="h-7 w-[1px] bg-slate-800 mx-1"></div>

        {/* User Account / Access Management Trigger */}
        <button
          onClick={onOpenAccessModal}
          className="flex items-center gap-2.5 pl-1 text-left hover:opacity-90 transition group"
          title="Account profile &amp; Access management"
        >
          <div className="h-9 w-9 rounded-full bg-gradient-to-tr from-teal-600 to-indigo-600 flex items-center justify-center text-white font-bold text-xs ring-2 ring-teal-500/30 group-hover:ring-teal-400 transition">
            {currentUser ? currentUser.avatarInitials : 'MS'}
          </div>
          <div className="hidden md:block text-left">
            <p className="text-xs font-semibold text-slate-200 leading-none">
              {currentUser ? currentUser.fullName : 'Dr. Marcus Sterling'}
            </p>
            <p className="text-[10px] text-teal-400 mt-1 flex items-center gap-1 font-mono">
              <UserCheck className="h-2.5 w-2.5" />
              <span>{currentUser ? currentUser.role.replace(/_/g, ' ') : 'Chief Medical Officer'}</span>
            </p>
          </div>
        </button>
      </div>
    </header>
  );
};
